import { OnInit, Component, ViewChild, ChangeDetectorRef } from '@angular/core';
import { filterPageTypes, toggleFilterTypes } from 'src/app/store/filters.era';
import { loadLocal } from 'src/app/helpers/helpers';
import { tableFilterTypes } from 'src/app/store/performers.era';
import { JoiEvent, Label, LabelObject } from 'globals/store/events.data';
import { traceNames } from 'src/app/services/performance.tracking.data';
import { ConfirmDeleteData, openConfirmDeleteGetResult } from 'src/app/components/dialog.confirmDelete.component';
import { addAResource, importTableData, saveTable } from '../performers/performerSharedFunctions';
import { openDialogGetResult } from 'src/app/helpers/angular-helpers';
import { ResourcesTableData, resourceColumns } from 'src/app/helpers/table-column-types';
import { TableColumn, TableMenuItem } from 'src/app/helpers/table.data';
import { resourceColumnsById } from 'src/app/helpers/table-helpers';
import { NestedTableComponent } from './../../components/nested-table/nested-table.component';
import { Wrapper } from 'src/app/components/wrapper.component';
import { LabelUpdateFunctionParams } from '../../components/label-marker/label-maker.dialog.component';
import { addLabelsToMany, removeLabelsFromMany } from '../../helpers/label-helpers';
import { JoiResource } from 'globals/store/resources.data';
import { resourcesTableReselector, resourceColumnsReselector } from '../../store/reselectors/resources.reselector';
import { ManageResourceData, ManageResourceDialogComponent } from './manage-resource';
import { EntitySubTable, resourceSubTableTypes } from 'globals/store/entity.data';
import { LoaderPaneFragmentComponent } from 'src/app/components/loader/loader-pane-fragment.component';
import { CommandMenuComponent } from 'src/app/components/nav-bar/command-menu.component';
import { CustomQuestionAnswersComponent } from 'src/app/components/entities/custom-questions-answers.component';
import { JoiNavWrapper } from 'src/app/components/wrappers/joi-nav-wrapper.component';
import { TopMenuComponent } from '../program/top.menu';
import { MobileMenuComponent } from 'src/app/components/menu/mobile-menu.component';
import { CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import { subTableNames } from 'globals/store/entity.data';
import { listDifference, listUnion } from 'globals/helpers/helpers';

@Component({
    selector: 'resources-table',
    template: `
<top-menu></top-menu>
<mobile-menu></mobile-menu>
<joi-nav-wrapper 
  class="icon-tabs"
  [simple]="true"
  [menuItems]="subTables"
  [menuMap]="subTableNames"
  [selectedId]="subTable" 
  (selectItem)="subTable = $event"
></joi-nav-wrapper>
<custom-question-answers *ngIf="subTable === 'responses'" [entityType]="'resource'"></custom-question-answers>
<command-menu
  *ngIf="subTable !== 'responses'"
  [filterPage]="filterPage"
  [filterType]="filterType"
  [columnType]="resourceSubTableTypes[subTable]"
  [editColumnsTitle]="('Edit ' + subTableNames[subTable] + ' Columns') | titlecase"
  [filterText]="'Filter resources'"
  [showDottedMenu]="true"
  [showExport]="true"
  [canEditColumns]="true"
  [addItemText]="!event || event.is_readonly ? '' : '+ ADD A RESOURCE'"
  (addItem)="addResource($event)"
  (onExport)="saveTable()"
  (onImport)="importTableData()"
></command-menu>
<nested-table
  *ngIf="columnData && subTable !== 'responses'"
  [items]="tableData"
  [filtersId]="filterPage"
  [condensedView]="condensed"
  [columns]="columnData[subTable]"
  [readOnly]="!event || event.is_readonly"
  [columnsById]="columnsById"
  [menuItems]="menuItems"
  [menuDisabled]="busy"
  [defaultSelectedColumnId]="resourceColumns.name"
  (selectItem)="manageResource($event)"
  (updatedSelectedIds)="selectIds($event)"
  [emptyMessage]="'No resources in this event'"
></nested-table>
<loader-pane-fragment [trace]="traceNames.viewResources"></loader-pane-fragment>
`,
    styleUrls: ['../../page.scss'],
    standalone: true,
    providers: [DatePipe],
    imports: [LoaderPaneFragmentComponent, CommonModule, NestedTableComponent, CommandMenuComponent, CustomQuestionAnswersComponent, JoiNavWrapper, TopMenuComponent, MobileMenuComponent, TitleCasePipe],
})
export class ResourcesTableComponent extends Wrapper<ResourcesTableComponent> implements OnInit {
  filterPage = filterPageTypes.resources
  filterType = tableFilterTypes.ResourcesTable
  resourceColumns = resourceColumns
  columnData: { [t in EntitySubTable]: TableColumn[] }
  columnsById: { [id: string]: TableColumn } = resourceColumnsById
  tableData: ResourcesTableData[] = []
  tableRowsById: { [id:string]: ResourcesTableData } = {}
  resources: { [eId: string]: JoiResource }
  resource_labels: LabelObject = {}
  selectedIds: string[] 
  subTableNames = subTableNames
  busy = false
  subTable: EntitySubTable = 'contactDetails'
  subTables: EntitySubTable[] = ['contactDetails', 'logistics', 'myView', 'responses' ]
  resourceSubTableTypes = resourceSubTableTypes
  
  allMenuItems: TableMenuItem[] = [
    { title: 'Delete', method: this.deleteResources.bind(this) },
    // { title: 'Add to clipboard', method: this.addToClipboard.bind(this) },
  ]
  
  menuItems: TableMenuItem[] = this.allMenuItems // default to showing all menu items 
  condensed: boolean
  event: JoiEvent
  traceNames = traceNames
  @ViewChild(NestedTableComponent) nestedTable: NestedTableComponent<ResourcesTableData>

  saveTable() {
    saveTable('resource', `${this.event.title} resources table`, this.dialog, this.nestedTable)
  }
  importTableData(){
    importTableData('resource', this.dialog, this.resourceService.addMultipleResources.bind(this.resourceService))
  }

  constructor(private changeDetectorRef: ChangeDetectorRef) {
    super()
    this.uiService.changePageTitle("Resources", true)
    this.subProp(this.store.select('events', 'event'), 'event')
    this.subProp(this.store.select('resources', 'entities'), 'resources')
    this.subProp(this.store.select('events', 'labelMaps', 'resource_labels'), 'resource_labels')
    this.selectCallback(resourcesTableReselector, null, (data: any) => {
      if (!data) {
        this.tableData = [] // for when the last is deleted
        return
      }
      const { resourceTableData, resourceTableRowsById } = data
      this.tableData = resourceTableData
      this.tableRowsById = resourceTableRowsById
    })
    this.loadCondensed()
    this.subCallback(this.store.select('filters', 'toggleFilters', this.filterPage, 'condensed'), _ => this.loadCondensed())
  }

  ngOnInit() {
    this.subCallback(this.store.select(resourceColumnsReselector), columnData => {
      this.columnData = columnData
    })
  }

  addResource(event) {
    addAResource(event, this.dialog, this.resourceService, this.manageResource.bind(this))
  }

  private get selectedResources(): JoiResource[] {
    return (this.selectedIds || []).map(id => this.resources[id])
  }

  async addToClipboard() {
    this.busy = true
    await this.resourceService.templateResources(this.selectedResources)
    this.busy = false
    const n = this.selectedResources.length
    this.nestedTable.setMessage(`Templates for ${n} resources${n > 1 ? 's' : ''} added to the clipboard.`)
    this.nestedTable.unselectAll()
  }

  async markAsNotConfirmed() {
    const selectedResources = this.selectedResources.filter(e => !!e.confirmed)
    selectedResources.forEach(r => r.confirmed = false)
    await this.resourceService.updateResources(selectedResources)
    this.setMenu()
  }

  async markAsConfirmed() {
    const selectedResources = this.selectedResources.filter(e => !!e.confirmed)
    selectedResources.forEach(r => r.confirmed = true)
    await this.resourceService.updateResources(selectedResources)
    this.setMenu()
  }

  setMenu() {
    setTimeout(() => {
      const selectedResources = this.selectedResources
      this.menuItems = [...this.allMenuItems]     
      if (selectedResources.some(r => r.labels?.length)) this.menuItems.unshift({ title: 'Remove labels', method: this.removeLabels.bind(this) })
      this.menuItems.unshift({ title: 'Add labels', method: this.addLabels.bind(this) })

      if (selectedResources.filter(r => !!r.confirmed).length) 
        this.menuItems.unshift({ title: 'Mark as not confirmed', method: this.markAsNotConfirmed.bind(this) })
      if (selectedResources.filter(r => !r.confirmed).length) 
        this.menuItems.unshift({ title: 'Mark as confirmed', method: this.markAsConfirmed.bind(this) })
    }, 0)
  }

  selectIds(ids: Set<string>) {
    this.selectedIds = Array.from(ids)
    this.setMenu()
  }

  
  public async updateLabelTemplate(labels: Label[]) {
    labels.forEach(label => this.resource_labels[label.id] = label) // need to overwrite the old values here to both maintain order and propagate changes without losing filtered out template labels
    await this.eventService.updateEvent(this.eventService.eventId, { resource_labels: Object.values(this.resource_labels) })
  }

  public async addLabelsUpdateFunction({ labels, labelTemplate, labelsToggled, labelsChanged }: LabelUpdateFunctionParams) {
    if (labelsChanged) this.updateLabelTemplate(labelTemplate)

    if (!labelsToggled) return
    const resources = this.selectedResources
      .filter(r => !r.labels || labels.some(label => !r.labels.includes(label)))
      .map(e => ({...e, labels: listUnion(e.labels, labels) }))
    
    await this.resourceService.updateResources(resources)
    this.setMenu()
  }

  public async removeLabelsUpdateFunction({ labels, labelTemplate, labelsToggled, labelsChanged }: LabelUpdateFunctionParams) {
    if (labelsChanged) this.updateLabelTemplate(labelTemplate)

    if (!labelsToggled) return
    const filteredLabels = labelTemplate.map(l => l.id).filter(id => !labels.includes(id))
    if (filteredLabels.length === 0) return

    const resources = this.selectedResources
      .filter(r => r.labels && r.labels.some(label => filteredLabels.includes(label)))
      .map(r => ({...r,  labels: listDifference(r.labels, filteredLabels) }))

    await this.resourceService.updateResources(resources)
    this.setMenu()    
  }

  async removeLabels(_, event) { 
    removeLabelsFromMany(this.removeLabelsUpdateFunction.bind(this), this.resource_labels, this.dialog, 'resource', this.selectedResources, event)
  }

  async addLabels(_, event) { 
    addLabelsToMany(this.addLabelsUpdateFunction.bind(this), this.resource_labels, this.dialog, 'resource', this.selectedResources, event)
  }

  async manageResource({ id }: ResourcesTableData) {
    if (this.busy) return
    console.log(`Should be opening the manage resource dialog for resource ${id}`)
    const data: ManageResourceData = { resourceId: id }
    this.changeDetectorRef.detach()
    await openDialogGetResult(this.dialog, ManageResourceDialogComponent, { autoFocus: false, disableClose: true, data, panelClass: 'full-screen-modal' })
    this.changeDetectorRef.reattach()
  }

  async deleteResources(toDelete: ResourcesTableData[], event) {
    const data: ConfirmDeleteData = {
      title: `Delete ${toDelete.length} resources`,
      rect: event.target.getBoundingClientRect(),
      para: `Are you sure you want to delete ${toDelete.length > 1 ? `all ${toDelete.length} selected resources` : 'the selected resource'}?`,
      dialog: this.dialog
    }
    const del = await openConfirmDeleteGetResult(data)
    if (!del) return
    this.busy = true
    await this.resourceService.deleteResources(toDelete.map(({ resource }) => resource))
    this.busy = false
  }

  loadCondensed() {
    this.condensed = loadLocal(`${this.filterPage}:${toggleFilterTypes.condensed}`, false)
  }

}
